import { AmputationType } from '@/constants/order/amputationType';
import { OrderStep } from '@/constants/order/constants';
import { OrderModel } from '@/store/models/Order';
import { MeasurementUnit, OrderDetailsMeasurementsModel } from '@/store/models/OrderDetails';
import { SocketBattery, SocketElectrodes } from '@/store/models/Socket';
import { PaginatedPayload } from '@/store/types';

export type OrderStatusAccepted =
	| 'accepted'
	| 'accepted_in_progress'
	| 'accepted_in_delivery'
	| 'accepted_delivered'
	| 'accepted_waiting_for_feedback';

export type OrderStatusRejected =
	| 'rejected'
	| 'rejected_incorrect_scan'
	| 'rejected_incorrect_attachments'
	| 'rejected_incorrect_measurements';

export type OrderStatusOther = 'drafted' | 'feedbacked' | 'placed' | 'archived';

export type OrderStatus = OrderStatusAccepted | OrderStatusRejected | OrderStatusOther;

export function isOrderRejected(value?: OrderStatus): value is OrderStatus {
	if (!value) return false;

	return [
		'rejected',
		'rejected_incorrect_scan',
		'rejected_incorrect_attachments',
		'rejected_incorrect_measurements',
	].includes(value as OrderStatus);
}

export interface CreateOrderPayload {
	prosthesis_id?: number;
	step: OrderStep;
	terminal_device_only?: boolean;
	// Poziom amputacji wybrany kaflem na ekranie glownym (GLA-60).
	initial_amputation_type?: AmputationType;
}

export interface UpdateOrderPayload {
	id: number;
	step: OrderStep;
	guid: string;
	address_id?: number;
	printing_way?: string;
}

export interface ChangeOrderStatusPayload {
	id: number;
	status: OrderStatus;
}

export interface FetchUserOrderListPayload {
	status: 'placed' | 'ready' | 'drafted' | 'feedbacked';
	page?: number | null;
	perPage?: number | null;
}

export interface FetchOrdersListPayload extends PaginatedPayload {
	status: OrderStatus;
}

export interface OrdersStatusCount {
	accepted?: number;
	archived?: number;
	draft?: number;
	placed?: number;
	rejected?: number;
}

interface WithGuid {
	guid: string;
}

interface WithStep {
	step: OrderStep;
}

export interface UpdateTerminalPayload extends WithStep, WithGuid {
	terminal_id: number[];
	terminals: Array<{
		terminal_id: number;
		terminal_glove_variant_id?: number | null;
		terminals_option_id?: number | null;
		special_finishing?: boolean | null;
		email?: string | null;
		buying_option?: string | null;
		notes?: string | null;
	}>;
}

export interface UpdateSocketPayload extends WithStep, WithGuid {
	bionic_hand_id: number;
	buying_option?: string;
	warranty?: string;
	color?: string;
	size?: string;
	notes?: string;
	electrodes?: SocketElectrodes;
	battery?: SocketBattery;
	etd?: string;
}

export interface UpdateColorPatternsPayload extends WithStep, WithGuid {
	color_pattern_id: number;
	special_finishing: boolean;
	special_finishing_description: string | null;
	color_patterns_option_id?: number | null;
}

export interface UpdateSocketDetailsPayload extends WithStep, WithGuid {
	socket_type?: string | null;
	outer_socket_type?: string;
	outer_socket_color?: string;
	outer_socket_color_description?: string;
	socket_type_finishing?: string | null;
	include_petg_socket?: boolean;
	printing_by_glaze?: boolean;
	petg_has_changes?: boolean;
	petg_changes_description?: string;
}

export interface UpdateDetailsPayload extends WithStep, WithGuid {
	printing_by_glaze?: string;
}

export interface UpdateSkinGlovePayload extends WithStep, WithGuid {
	skin_glove_id: number;
	skin_glove_details: {
		catalog_number: string;
		size: string;
		color: string;
		notes: string;
	};
}

export interface UpdateElbowPayload extends WithStep, WithGuid {
	/**
	 * Opcjonalne od GLA-63: przy Espire łokcia nie wybiera protetyk — jest nim sam produkt,
	 * a serwer rozwiązuje wiersz katalogu z powiązania `prosthesis_elbow`.
	 */
	elbow_id?: number;
	size?: string;
	color?: string;
	counterbalance_dial_side?: string;
}

export interface UpdateOrderDetailsPayload extends WithStep, WithGuid {
	order_details: {
		unit_of_measure: MeasurementUnit;
		measurements: OrderDetailsMeasurementsModel;
		body_side: string;
		clients_name: string;
		amputation_type?: AmputationType;
		additional_notes?: string;
		already_owned: boolean;
		valve_opening?: boolean;
		printing_by_glaze?: boolean;
	};
}

export interface UpdateScansPayload extends WithStep, WithGuid {
	frontal_angle?: number;
	sagittal_angle?: number;
	scanner_type?: string | null;
	scan_type: string;
}

export interface UpdatePhotosPayload extends WithStep, WithGuid {
	photos: File[];
}

export type UpdateOrderModel = { guid: string } & Partial<OrderModel>;

export interface SearchOrdersPayload {
	query: string;
	status: OrderStatus;
}
